import { useState } from 'react';
import { format } from 'date-fns';
import { validateTimeRange, getMinEndTime } from '@/utils/timeValidation';

interface EventFormProps {
  selectedDate: Date;
  selectedTime: string | undefined;
  selectedEndTime: string | undefined;
  onClose: () => void;
  onSave: (event: {
    title: string;
    description: string;
    date: string;
    start_time: string;
    end_time: string;
    type: 'reservation' | 'event' | 'task';
  }) => void;
}

export default function EventForm({ selectedDate, selectedTime, selectedEndTime, onClose, onSave }: EventFormProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [startTime, setStartTime] = useState(selectedTime || '08:00');
  const [endTime, setEndTime] = useState(selectedEndTime || (() => {
    const [hours, minutes] = (selectedTime || '08:00').split(':').map(Number);
    const date = new Date();
    date.setHours(hours + 1);
    date.setMinutes(minutes);
    return format(date, 'HH:mm');
  }));
  const [type, setType] = useState<'reservation' | 'event' | 'task'>('event');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateTimeRange(startTime, endTime)) {
      return; // Prevent submission if times are invalid
    }

    onSave({
      title,
      description,
      date: format(selectedDate, 'yyyy-MM-dd'),
      start_time: startTime,
      end_time: endTime,
      type
    });
  };

  const handleStartTimeChange = (newStartTime: string) => {
    setStartTime(newStartTime);
    if (!validateTimeRange(newStartTime, endTime)) {
      setEndTime(getMinEndTime(newStartTime));
    }
  };

  const handleEndTimeChange = (newEndTime: string) => {
    if (validateTimeRange(startTime, newEndTime)) {
      setEndTime(newEndTime);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-dark-bg-light rounded-lg w-full max-w-md shadow-xl">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-border flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary">
            Add Event for {format(selectedDate, 'MMMM d, yyyy')}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 dark:text-dark-text-secondary hover:bg-gray-100 dark:hover:bg-dark-bg p-2 rounded-full transition-colors"
          >
            <i className="fas fa-times" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
              Event Type
            </label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as any)}
              className="select w-full bg-white dark:bg-dark-bg text-gray-900 dark:text-dark-text-primary border-2 border-gray-200 dark:border-dark-border rounded-lg focus:border-primary-500 dark:focus:border-primary-500 px-4 h-11"
            >
              <option value="reservation">Reservation</option>
              <option value="event">Event</option>
              <option value="task">Task</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
              Title
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="input w-full bg-white dark:bg-dark-bg text-gray-900 dark:text-dark-text-primary border-2 border-gray-200 dark:border-dark-border rounded-lg focus:border-primary-500 dark:focus:border-primary-500 px-4 h-11"
              required
            />
          </div>

          <div className="flex gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
                Start Time
              </label>
              <div className="relative">
                <input
                  type="time"
                  value={startTime}
                  onChange={(e) => handleStartTimeChange(e.target.value)}
                  className="input w-full bg-white dark:bg-dark-bg text-gray-900 dark:text-dark-text-primary border-2 border-gray-200 dark:border-dark-border rounded-lg focus:border-primary-500 dark:focus:border-primary-500 px-4 h-11"
                  style={{ colorScheme: 'light dark' }}
                  required
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <i className="fas fa-clock text-gray-400" />
                </div>
              </div>
            </div>

            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
                End Time
              </label>
              <div className="relative">
                <input
                  type="time"
                  value={endTime}
                  onChange={(e) => handleEndTimeChange(e.target.value)}
                  className="input w-full bg-white dark:bg-dark-bg text-gray-900 dark:text-dark-text-primary border-2 border-gray-200 dark:border-dark-border rounded-lg focus:border-primary-500 dark:focus:border-primary-500 px-4 h-11"
                  style={{ colorScheme: 'light dark' }}
                  required
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <i className="fas fa-clock text-gray-400" />
                </div>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="textarea w-full h-24 bg-white dark:bg-dark-bg text-gray-900 dark:text-dark-text-primary border-2 border-gray-200 dark:border-dark-border rounded-lg focus:border-primary-500 dark:focus:border-primary-500 p-4 resize-none"
            />
          </div>

          {/* Footer */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-outline btn-outline-secondary"
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn btn-primary"
            >
              Save Event
            </button>
          </div>
        </form>
      </div>
    </div>
  );
} 